import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Avatar,
  IconButton,
  Badge,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  MenuItem
} from '@mui/material';
import {
  Send,
  Chat,
  MoreVert,
  AttachFile,
  EmojiEmotions
} from '@mui/icons-material';
import { getFirestore, collection, query, where, getDocs, doc, getDoc, orderBy, limit } from 'firebase/firestore';

interface Message {
  id: string;
  senderId: string;
  senderName: string;
  senderAvatar?: string;
  content: string;
  timestamp: Date;
  type: 'text' | 'image' | 'system';
  rentalId?: string;
}

interface Conversation {
  id: string;
  participantId: string;
  participantName: string;
  participantAvatar?: string;
  rentalId?: string;
  dressName?: string;
  lastMessage?: Message;
  unreadCount: number;
  isActive: boolean;
  reservationDetails?: {
    startDate: string;
    endDate: string;
    pickupAddress: string;
    returnAddress: string;
    totalPrice: number;
    status: 'confirmed' | 'active' | 'completed';
  };
}

interface ChatInterfaceProps {
  currentUserId: string;
  currentUserName: string;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  currentUserId,
  currentUserName
}) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Charger les conversations depuis Firestore
  useEffect(() => {
    const loadConversations = async () => {
      try {
        setLoading(true);
        const db = getFirestore();
        
        // Charger les conversations où l'utilisateur est participant
        const conversationsQuery = query(
          collection(db, 'conversations'),
          where('participants', 'array-contains', currentUserId)
        );
        
        const conversationsSnapshot = await getDocs(conversationsQuery);
        const conversationsData: Conversation[] = [];
        
        for (const convDoc of conversationsSnapshot.docs) {
          const convData = convDoc.data();
          const otherParticipantId = convData.participants.find((id: string) => id !== currentUserId);
          
          if (otherParticipantId) {
            // Charger les infos de l'autre participant
            const otherUserDoc = await getDoc(doc(db, 'users', otherParticipantId));
            const otherUserData = otherUserDoc.exists() ? otherUserDoc.data() : null;
            
            // Charger le dernier message
            const messagesQuery = query(
              collection(db, 'conversations', convDoc.id, 'messages'),
              orderBy('timestamp', 'desc'),
              limit(1)
            );
            const lastMessageSnapshot = await getDocs(messagesQuery);
            const lastMessageData = lastMessageSnapshot.docs[0]?.data();
            
            conversationsData.push({
              id: convDoc.id,
              participantId: otherParticipantId,
              participantName: otherUserData?.displayName || otherUserData?.name || 'Utilisateur',
              participantAvatar: otherUserData?.photoURL || otherUserData?.avatar || '',
              rentalId: convData.rentalId,
              dressName: convData.dressName || '',
              lastMessage: lastMessageData ? {
                id: lastMessageSnapshot.docs[0].id,
                senderId: lastMessageData.senderId,
                senderName: lastMessageData.senderName,
                content: lastMessageData.content,
                timestamp: lastMessageData.timestamp?.toDate() || new Date(),
                type: lastMessageData.type || 'text'
              } : undefined,
              unreadCount: convData.unreadCount?.[currentUserId] || 0,
              isActive: convData.isActive || false,
              reservationDetails: convData.reservationDetails
            });
          }
        }
        
        setConversations(conversationsData);
      } catch (error) {
        console.error('Erreur lors du chargement des conversations:', error);
        setConversations([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (currentUserId) {
      loadConversations();
    }
  }, [currentUserId, currentUserName]);

  useEffect(() => {
    if (selectedConversation) {
      // Charger les messages depuis Firestore
      const loadMessages = async () => {
        try {
          const db = getFirestore();
          const messagesQuery = query(
            collection(db, 'conversations', selectedConversation.id, 'messages'),
            orderBy('timestamp', 'asc')
          );
          
          const messagesSnapshot = await getDocs(messagesQuery);
          const messagesData: Message[] = [];
          
          messagesSnapshot.forEach((msgDoc) => {
            const msgData = msgDoc.data();
            messagesData.push({
              id: msgDoc.id,
              senderId: msgData.senderId,
              senderName: msgData.senderName,
              content: msgData.content,
              timestamp: msgData.timestamp?.toDate() || new Date(),
              type: msgData.type || 'text'
            });
          });
          
          setMessages(messagesData);
        } catch (error) {
          console.error('Erreur lors du chargement des messages:', error);
          setMessages([]);
        }
      };
      
      loadMessages();
    } else {
      setMessages([]);
    }
  }, [selectedConversation, currentUserId, currentUserName]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = () => {
    if (!newMessage.trim() || !selectedConversation) return;

    const message: Message = {
      id: `msg_${Date.now()}`,
      senderId: currentUserId,
      senderName: currentUserName,
      content: newMessage.trim(),
      timestamp: new Date(),
      type: 'text'
    };

    setMessages(prev => [...prev, message]);
    setNewMessage('');

    // Update conversation with new last message
    setConversations(prev => 
      prev.map(conv => 
        conv.id === selectedConversation.id 
          ? { ...conv, lastMessage: message, unreadCount: 0 }
          : conv
      )
    );
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (date: Date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Aujourd\'hui';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Hier';
    } else {
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short'
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Chargement des conversations...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Conversations List */}
      <Box sx={{ 
        width: 350, 
        backgroundColor: 'white', 
        borderRight: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ color: '#a5816d', fontWeight: 'bold' }}>
            Messages
          </Typography>
        </Box>

        <List sx={{ flex: 1, overflow: 'auto' }}>
          {conversations.map((conversation) => (
            <ListItem
              key={conversation.id}
              button
              onClick={() => setSelectedConversation(conversation)}
              sx={{
                backgroundColor: conversation.id === selectedConversation?.id ? '#f0f0f0' : 'transparent',
                borderLeft: conversation.id === selectedConversation?.id ? '3px solid #a5816d' : '3px solid transparent',
                '&:hover': {
                  backgroundColor: '#f8f8f8'
                }
              }}
            >
              <Box sx={{ position: 'relative', mr: 2 }}>
                <Avatar 
                  src={conversation.participantAvatar} 
                  alt={conversation.participantName}
                  sx={{ width: 50, height: 50 }}
                />
                {conversation.unreadCount > 0 && (
                  <Badge
                    badgeContent={conversation.unreadCount}
                    color="error"
                    sx={{
                      position: 'absolute',
                      top: -5,
                      right: -5
                    }}
                  />
                )}
              </Box>

              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {conversation.participantName}
                    </Typography>
                    {conversation.lastMessage && (
                      <Typography variant="caption" color="text.secondary">
                        {formatTime(conversation.lastMessage.timestamp)}
                      </Typography>
                    )}
                  </Box>
                }
                secondary={
                  <Box>
                    {conversation.dressName && (
                      <Chip 
                        label={conversation.dressName} 
                        size="small" 
                        sx={{ 
                          mb: 0.5, 
                          fontSize: '0.7rem',
                          backgroundColor: '#a5816d',
                          color: 'white'
                        }} 
                      />
                    )}
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontWeight: conversation.unreadCount > 0 ? 'bold' : 'normal'
                      }}
                    >
                      {conversation.lastMessage?.content || 'Aucun message'}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Chat Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'white', 
              borderBottom: '1px solid #e0e0e0'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar 
                    src={selectedConversation.participantAvatar} 
                    alt={selectedConversation.participantName}
                    sx={{ mr: 2 }}
                  />
                  <Box>
                    <Typography variant="h6">
                      {selectedConversation.participantName}
                    </Typography>
                    {selectedConversation.dressName && (
                      <Typography variant="body2" color="text.secondary">
                        À propos de: {selectedConversation.dressName}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <IconButton>
                  <MoreVert />
                </IconButton>
              </Box>
              
              {/* Reservation Details */}
              {selectedConversation.reservationDetails && (
                <Paper sx={{ p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#a5816d' }}>
                    📅 Détails de la réservation
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, fontSize: '0.875rem' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Date de début:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {new Date(selectedConversation.reservationDetails.startDate).toLocaleDateString('fr-FR')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Date de retour:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {new Date(selectedConversation.reservationDetails.endDate).toLocaleDateString('fr-FR')}
                      </Typography>
                    </Box>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="caption" color="text.secondary">
                        Adresse de récupération:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {selectedConversation.reservationDetails.pickupAddress}
                      </Typography>
                    </Box>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="caption" color="text.secondary">
                        Adresse de retour:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {selectedConversation.reservationDetails.returnAddress}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Prix total:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#a5816d' }}>
                        {selectedConversation.reservationDetails.totalPrice}€
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <Chip 
                        label={selectedConversation.reservationDetails.status === 'confirmed' ? 'Confirmée' : 
                               selectedConversation.reservationDetails.status === 'active' ? 'En cours' : 'Terminée'}
                        size="small"
                        sx={{ 
                          backgroundColor: selectedConversation.reservationDetails.status === 'confirmed' ? '#e3f2fd' : 
                                          selectedConversation.reservationDetails.status === 'active' ? '#fff3e0' : '#e8f5e8',
                          color: selectedConversation.reservationDetails.status === 'confirmed' ? '#1976d2' : 
                                 selectedConversation.reservationDetails.status === 'active' ? '#f57c00' : '#388e3c'
                        }}
                      />
                    </Box>
                  </Box>
                  
                  {/* Report Problem Button */}
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ 
                        borderColor: '#ff5722', 
                        color: '#ff5722',
                        '&:hover': {
                          borderColor: '#d84315',
                          backgroundColor: '#ffebee'
                        }
                      }}
                      onClick={() => setShowReportDialog(true)}
                    >
                      🚨 Signaler un problème
                    </Button>
                  </Box>
                </Paper>
              )}
            </Box>

            {/* Messages */}
            <Box sx={{ 
              flex: 1, 
              overflow: 'auto', 
              p: 2,
              backgroundColor: '#fafafa'
            }}>
              {messages.map((message, index) => {
                const isOwn = message.senderId === currentUserId;
                const showDate = index === 0 || 
                  formatDate(message.timestamp) !== formatDate(messages[index - 1].timestamp);

                return (
                  <Box key={message.id}>
                    {showDate && (
                      <Box sx={{ textAlign: 'center', my: 2 }}>
                        <Chip 
                          label={formatDate(message.timestamp)}
                          size="small"
                          sx={{ backgroundColor: '#e0e0e0' }}
                        />
                      </Box>
                    )}
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: isOwn ? 'flex-end' : 'flex-start',
                      mb: 1
                    }}>
                      <Paper
                        sx={{
                          p: 2,
                          maxWidth: '70%',
                          backgroundColor: isOwn ? '#a5816d' : 'white',
                          color: isOwn ? 'white' : 'text.primary',
                          borderRadius: isOwn ? '18px 18px 4px 18px' : '18px 18px 18px 4px'
                        }}
                      >
                        {!isOwn && (
                          <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, display: 'block' }}>
                            {message.senderName}
                          </Typography>
                        )}
                        <Typography variant="body1">
                          {message.content}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block', 
                            textAlign: 'right',
                            mt: 0.5,
                            opacity: 0.7
                          }}
                        >
                          {formatTime(message.timestamp)}
                        </Typography>
                      </Paper>
                    </Box>
                  </Box>
                );
              })}
              <div ref={messagesEndRef} />
            </Box>

            {/* Message Input */}
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'white', 
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1
            }}>
              <IconButton color="primary">
                <AttachFile />
              </IconButton>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Tapez votre message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '20px',
                    backgroundColor: '#f5f5f5'
                  }
                }}
              />
              <IconButton color="primary">
                <EmojiEmotions />
              </IconButton>
              <Button
                variant="contained"
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
                sx={{
                  backgroundColor: '#a5816d',
                  '&:hover': { backgroundColor: '#8B7355' },
                  borderRadius: '50%',
                  minWidth: 'auto',
                  width: 40,
                  height: 40
                }}
              >
                <Send />
              </Button>
            </Box>
          </>
        ) : (
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flexDirection: 'column'
          }}>
            <Chat sx={{ fontSize: 80, color: '#e0e0e0', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Sélectionnez une conversation pour commencer
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Vos conversations avec les propriétaires de robes apparaîtront ici
            </Typography>
          </Box>
        )}
      </Box>

      {/* Report Problem Dialog */}
      <Dialog 
        open={showReportDialog} 
        onClose={() => setShowReportDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#fff3e0', color: '#e65100' }}>
          🚨 Signaler un problème
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Si vous rencontrez un problème avec votre réservation, merci de nous le signaler. 
            Nous examinerons votre demande dans les plus brefs délais.
          </Alert>
          
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              label="Type de problème"
              select
              fullWidth
              defaultValue=""
              variant="outlined"
              sx={{ mb: 2 }}
            >
              <MenuItem value="damage">Dommage à la robe</MenuItem>
              <MenuItem value="delivery">Problème de livraison</MenuItem>
              <MenuItem value="size">Problème de taille</MenuItem>
              <MenuItem value="quality">Problème de qualité</MenuItem>
              <MenuItem value="communication">Problème de communication</MenuItem>
              <MenuItem value="other">Autre</MenuItem>
            </TextField>

            <TextField
              label="Description détaillée"
              multiline
              rows={4}
              fullWidth
              placeholder="Décrivez le problème rencontré..."
              variant="outlined"
              sx={{ mb: 2 }}
            />

            <TextField
              label="Montant estimé du dommage (optionnel)"
              type="number"
              fullWidth
              placeholder="0"
              variant="outlined"
              InputProps={{
                endAdornment: <Typography variant="body2" color="text.secondary">€</Typography>
              }}
              sx={{ mb: 2 }}
            />

            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Note:</strong> Si un dommage est confirmé, le montant pourra être débité 
                de votre carte de paiement selon les conditions générales.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setShowReportDialog(false)}
            color="inherit"
          >
            Annuler
          </Button>
          <Button 
            variant="contained"
            sx={{ 
              backgroundColor: '#ff5722',
              '&:hover': { backgroundColor: '#d84315' }
            }}
          >
            Envoyer le signalement
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatInterface;
